import React, { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Button, DatePicker, Form, Modal, Steps } from "antd";
import dayjs, { type Dayjs } from "dayjs";

import { exercisesActions, traineeActivitiesActions, workoutTemplatesActions } from "@fitness-app/app-store";
import { type ProgramWorkout } from "@fitness-app/data-models/entities/TrainingProgram";
import { getErrorMessage } from "@fitness-app/utils";
import { createProgramWorkoutTemplate } from "@fitness-app/utils/src/programs/workoutsTemplate";

import { useEntityChange } from "~/hooks/useEntityChange";
import { SelectedUsersList } from "~/modules/Chat/SendMessageToMultipleUsers/SelectedUsersList";
import { type SelectedUsersListItem } from "~/modules/Chat/SendMessageToMultipleUsers/types";
import BatchWorkoutDayBuilder from "~/modules/Trainee/BatchAddWorkout/BatchWorkoutDayBuilder";
import { useAppDispatch } from "~/store/initializeStore";

interface BatchAddWorkoutModalProps {
  open: boolean;
  onClose: () => void;
}

enum Step {
  SelectUsers = 0,
  BuildWorkout = 1,
  Confirm = 2,
}

export const BatchAddWorkoutModal = ({ open, onClose }: BatchAddWorkoutModalProps): React.ReactElement => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [loading, onSuccess, onFailure, onStart] = useEntityChange(onClose);

  const [currentStep, setStep] = useState<Step>(Step.SelectUsers);
  const [selectedUsers, setSelectedUsers] = useState<SelectedUsersListItem[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagCondition, setTagCondition] = useState<"hasTag" | "notHasTag" | false>(false);
  const [eventDate, setEventDate] = useState<Dayjs>(dayjs());
  const [workout, setWorkout] = useState<ProgramWorkout>(() => createProgramWorkoutTemplate());

  useEffect(() => {
    if (!open) {
      setStep(Step.SelectUsers);
      setSelectedUsers([]);
      setSelectedTags([]);
      setTagCondition(false);
      setEventDate(dayjs());
      setWorkout(createProgramWorkoutTemplate());
    } else {
      void dispatch(exercisesActions.fetchExercises());
      void dispatch(workoutTemplatesActions.fetchWorkoutTemplates());
    }
  }, [open]);

  const totalExercises = useMemo(
    () => workout.exercises.length + workout.preWorkout.length + workout.postWorkout.length,
    [workout],
  );

  const usingTags = Boolean(tagCondition && selectedTags.length > 0);
  const canGoToWorkoutStep = selectedUsers.length > 0 || usingTags;
  const canGoToConfirmStep = totalExercises > 0;

  const handleSubmit = async () => {
    try {
      onStart(undefined, "Kolejkowanie treningów...");
      const basePayload = {
        eventDate: eventDate.format("YYYY-MM-DD"),
        workoutTemplate: workout,
      };
      const payload = usingTags
        ? { ...basePayload, tags: selectedTags, condition: tagCondition as "hasTag" | "notHasTag" }
        : { ...basePayload, traineeIds: selectedUsers.map((user) => user.id) };

      const response = await dispatch(traineeActivitiesActions.batchAddWorkoutToTrainees(payload)).unwrap();

      void queryClient.invalidateQueries({ queryKey: ["calendar_events"] });

      onSuccess(`Zakolejkowano dodanie treningu dla ${response.queued} klientów`, "success");
    } catch (error) {
      onFailure(getErrorMessage(error));
    }
  };

  const renderStepContent = () => {
    if (currentStep === Step.SelectUsers) {
      return (
        <SelectedUsersList
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
          changeCondition={setTagCondition}
          condition={tagCondition}
          sendToMarketingChannelIfExists={false}
          toggleSendToMarketingChannel={() => undefined}
          hideMarketingChannelOption
        />
      );
    }

    if (currentStep === Step.BuildWorkout) {
      return (
        <div className="flex flex-col gap-4">
          <Form layout="vertical">
            <Form.Item label="Data treningu" required>
              <DatePicker
                value={eventDate}
                onChange={(value) => value && setEventDate(value)}
                format="DD-MM-YYYY"
                allowClear={false}
                style={{ width: 220 }}
              />
            </Form.Item>
          </Form>
          <Alert
            type="info"
            showIcon
            message={
              usingTags
                ? `Wybór po tagach (${tagCondition === "hasTag" ? "posiada" : "nie posiada"}: ${selectedTags.join(", ")}). Lista klientów zostanie wyliczona po stronie serwera.`
                : `Wybranych klientów: ${selectedUsers.length}. Trening zostanie dodany każdemu z nich w wybranym dniu.`
            }
          />
          <BatchWorkoutDayBuilder workout={workout} onChange={setWorkout} />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-3">
        <Alert
          type="warning"
          showIcon
          message="Czy na pewno chcesz dodać trening do wybranych klientów?"
          description="Operacja zostanie zakolejkowana w tle. Po wykonaniu trening pojawi się w kalendarzu każdego z klientów."
        />
        <ul className="m-0 pl-5">
          {usingTags ? (
            <li>
              <strong>Filtr po tagach:</strong> {tagCondition === "hasTag" ? "posiada" : "nie posiada"} -{" "}
              {selectedTags.join(", ")}
            </li>
          ) : (
            <li>
              <strong>Klientów:</strong> {selectedUsers.length}
            </li>
          )}
          <li>
            <strong>Data:</strong> {eventDate.format("DD-MM-YYYY")}
          </li>
          <li>
            <strong>Nazwa treningu:</strong> {workout.name}
          </li>
          <li>
            <strong>Liczba ćwiczeń:</strong> {totalExercises}
          </li>
        </ul>
      </div>
    );
  };

  const stepsConfig = [{ title: "Wybór klientów" }, { title: "Budowanie treningu" }, { title: "Podsumowanie" }];

  const isLastStep = currentStep === Step.Confirm;

  const goNext = () => {
    if (currentStep === Step.SelectUsers && !canGoToWorkoutStep) return;
    if (currentStep === Step.BuildWorkout && !canGoToConfirmStep) return;
    setStep((prev) => (prev + 1) as Step);
  };

  return (
    <Modal
      destroyOnClose
      open={open}
      title="Dodaj trening do wielu klientów"
      onCancel={onClose}
      width={1000}
      footer={
        <div className="flex items-center justify-end gap-3">
          {currentStep > Step.SelectUsers && (
            <Button onClick={() => setStep((prev) => (prev - 1) as Step)} disabled={!!loading}>
              Cofnij
            </Button>
          )}
          {!isLastStep && (
            <Button
              type="primary"
              onClick={goNext}
              disabled={
                (currentStep === Step.SelectUsers && !canGoToWorkoutStep) ||
                (currentStep === Step.BuildWorkout && !canGoToConfirmStep)
              }
            >
              Dalej
            </Button>
          )}
          {isLastStep && (
            <Button type="primary" onClick={handleSubmit} loading={!!loading}>
              Zakolejkuj
            </Button>
          )}
        </div>
      }
    >
      <div className="py-3">
        <Steps current={currentStep} items={stepsConfig} />
      </div>
      <div>{renderStepContent()}</div>
    </Modal>
  );
};

export default BatchAddWorkoutModal;
